import React, { useEffect, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as actions from '../../store/actions';
import { readStoredUser } from '../../utils/auth/userStorage';
import { EPATHS } from '../../routes/paths.enum';

/**
 * SessionExpiredOrAccessDenied — Guard pour routes protégées (admin/auth).
 *
 * Différencie 2 cas qui produisaient autrefois un `<Redirect>` silencieux :
 *
 *   1. Session expirée / absente (`_user` localStorage = null OU isVerified = false)
 *      → `setIsTimeout(true)` AVANT le redirect home → SessionTimeoutModal (mountée dans Header)
 *      s'affiche par-dessus la home → user voit "Session Timeout - Reconnect ▸" au lieu d'un
 *      redirect silencieux.
 *
 *   2. Authenticated mais permissions insuffisantes (ex. admin requis, user roleId !== 1)
 *      → redirect home silencieux (légitime — pas une expiration de session).
 *
 * Le redirect cible toujours `EPATHS.HOME` ; la pathname courante est mémorisée par
 * SessionTimeoutModal.handleReconnect via localStorage._redirectAfterLogin (cf. login flow).
 *
 * Bug fix 2026-06-06 (TRACK setting-redirect-fix) : avant, `<Redirect>` silencieux quand
 * `_user` était cleared (session JWT expirée naturellement). User perdait la page courante
 * sans aucune indication. Maintenant : SessionTimeoutModal explicite.
 *
 * @see src/pages/Modals/SessionTimeoutModal.jsx
 * @see src/utils/functions/apiCallWithToken.js (handler 401 réutilise setIsTimeout)
 */
function SessionExpiredOrAccessDenied() {
  const dispatch = useDispatch();
  /* Capture SYNCHRONE de la pathname au 1er render — le useEffect tirerait APRÈS le
     <Redirect> synchrone (pathname déjà = '/'). useRef garde la valeur initiale. */
  const originalPath = useRef(window.location.pathname + window.location.search);

  useEffect(() => {
    const parsed = readStoredUser();
    // FIX 2026-06-30 — présence de session = accessToken, PAS isVerified.
    // Depuis SPEC-2026-06-29-auth-confirm-2fa.v1, un compte wallet connecté est isVerified=false
    // tant que son email n'est pas validé. Se baser sur isVerified traiterait à tort un user wallet
    // pleinement loggué comme « session expirée » → redirect home. isVerified gate les actions (403),
    // pas la navigation. Le signal de session fiable est accessToken.
    const isAuth = !!(parsed && parsed.accessToken);

    if (!isAuth) {
      // Cas 1 : session expirée / jamais loggué.
      // Mémoriser la pathname d'origine pour retour post-reconnexion (login() lit _redirectAfterLogin).
      // PLUS de setIsTimeout → plus de modal. Redirect home = comportement naturel pour les
      // pages protégées. Le silent logout a déjà été fait par apiCallWithToken.js / sessionCheck.
      try {
        const fullPath = originalPath.current;
        // Ne pas mémoriser les sous-pages support (/support/:id) — après login l'utilisateur
        // doit arriver sur /support (liste), pas sur un ticket spécifique inaccessible sans auth.
        const isSupportDetail = /^\/support\/.+/.test(fullPath);
        if (fullPath && fullPath !== '/' && fullPath !== EPATHS.HOME && !isSupportDetail) {
          localStorage.setItem('_redirectAfterLogin', fullPath);
        }
      } catch (e) {}
      // dispatch(actions.setIsTimeout(true)); — supprimé : silent logout remplace la modal
    }
    // Cas 2 (isAuth=true mais permissions insuffisantes) : silencieux, pas de modal
  }, [dispatch]);

  return <Redirect to={EPATHS.HOME} />;
}

export default SessionExpiredOrAccessDenied;
